import React from 'react';
import { observer } from 'mobx-react-lite';

import { FormControl, MenuItem, Select } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BusinessIcon from '@mui/icons-material/Business';

import { DefaultDocumentAccess } from 'utils/api/modules/entities/entities.types';

import {
  GeneralAccessSection as StyledSection,
  ShareDialogSectionTitle,
  GeneralAccessRow,
  GeneralAccessInfo,
  GeneralAccessIcon,
  GeneralAccessLabel,
  GeneralAccessTitle,
  GeneralAccessHint,
  OwnerLabel,
} from './styles';

interface GeneralAccessSectionProps {
  defaultAccess: DefaultDocumentAccess;
  isOwnerOrAdmin: boolean;
  onUpdate: (defaultAccess: DefaultDocumentAccess) => Promise<boolean>;
}

const HINTS: Record<DefaultDocumentAccess, string> = {
  [DefaultDocumentAccess.Restricted]: 'Only people added above can access',
  [DefaultDocumentAccess.AllView]: 'Everyone in your organisation can view',
  [DefaultDocumentAccess.AllEdit]: 'Everyone in your organisation can edit',
};

const GeneralAccessSection: React.FC<GeneralAccessSectionProps> = observer(
  ({ defaultAccess, isOwnerOrAdmin, onUpdate }) => {
    const isRestricted = defaultAccess === DefaultDocumentAccess.Restricted;

    return (
      <StyledSection>
        <ShareDialogSectionTitle>General access</ShareDialogSectionTitle>
        <GeneralAccessRow>
          <GeneralAccessInfo>
            <GeneralAccessIcon>
              {isRestricted ? <LockOutlinedIcon sx={{ fontSize: 20 }} /> : <BusinessIcon sx={{ fontSize: 20 }} />}
            </GeneralAccessIcon>
            <GeneralAccessLabel>
              <GeneralAccessTitle>{isRestricted ? 'Restricted' : 'Anyone in organisation'}</GeneralAccessTitle>
              <GeneralAccessHint>{HINTS[defaultAccess]}</GeneralAccessHint>
            </GeneralAccessLabel>
          </GeneralAccessInfo>
          {isOwnerOrAdmin ? (
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={defaultAccess} onChange={(e) => onUpdate(e.target.value as DefaultDocumentAccess)}>
                <MenuItem value={DefaultDocumentAccess.Restricted}>Restricted</MenuItem>
                <MenuItem value={DefaultDocumentAccess.AllView}>Anyone can view</MenuItem>
                <MenuItem value={DefaultDocumentAccess.AllEdit}>Anyone can edit</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <OwnerLabel>
              {isRestricted ? 'Restricted' : defaultAccess === DefaultDocumentAccess.AllEdit ? 'Can edit' : 'Can view'}
            </OwnerLabel>
          )}
        </GeneralAccessRow>
      </StyledSection>
    );
  }
);

export default GeneralAccessSection;
